import { useMemo, useState } from 'react'

import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import { styled, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'

import Icon from 'src/@core/components/icon'

import { useAuth } from 'src/hooks/useAuth'

import TypeBadge from 'src/components/course-overview/components/TypeBadge'
import { ModuleLesson } from 'src/components/customer/courses/course-details/ModuleLesson'

import { LessonsAndSubmodules, Submodule } from 'src/types/courses'

const Container = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
  maxWidth: '91%',
  marginLeft: 'auto',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(6),
  backgroundColor: theme.palette.background.paper
}))

export const BadgeWrapper = styled(Stack, {
  shouldForwardProp: prop => prop !== 'isSubmodule'
})<{ isSubmodule: boolean }>(({ theme, isSubmodule }) => ({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
  zIndex: 3,
  ...(isSubmodule
    ? {
        top: theme.spacing(8),
        left: theme.spacing(2)
      }
    : {
        bottom: theme.spacing(1),
        right: theme.spacing(1)
      })
}))

type ModuleContentProps = {
  lessonsAndSubmodules: LessonsAndSubmodules[]
  handleClickLesson: (lessonId: number) => () => Promise<void>
}

type SubmoduleSectionProps = {
  submodule: Submodule | null | undefined
  isPreviewMode: boolean
  handleClickLesson: (lessonId: number) => () => Promise<void>
  lessonsAndSubmodules: LessonsAndSubmodules[]
}

const SubmoduleSection = ({
  submodule,
  isPreviewMode,
  handleClickLesson,
  lessonsAndSubmodules
}: SubmoduleSectionProps) => {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(true)

  return (
    <Stack gap={theme.spacing(2)}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        position='relative'
        paddingY={theme.spacing(2)}
        paddingX={theme.spacing(4)}
        onClick={() => setIsOpen(prev => !prev)}
        sx={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <Stack direction='row' alignItems='center' gap={theme.spacing(2)}>
          <Icon
            icon='tabler:chevron-right'
            fontSize={20}
            style={{
              transition: 'transform 0.2s ease-in-out',
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)'
            }}
          />
          <Typography variant='h6'>{submodule?.title}</Typography>
        </Stack>

        {isPreviewMode && (
          <BadgeWrapper theme={theme} isSubmodule>
            {!submodule?.is_published && <TypeBadge type={'draft'} />}
          </BadgeWrapper>
        )}
      </Stack>

      <Collapse in={isOpen}>
        <Stack
          maxWidth={'93%'}
          width={'100%'}
          marginLeft={'auto'}
          marginBottom={theme.spacing(2)}
          gap={theme.spacing(2)}
        >
          {submodule?.lessons?.map((lesson, index) => (
            <ModuleLesson
              key={lesson.id}
              content={lesson}
              index={index}
              handleClickLesson={handleClickLesson}
              lessonsAndSubmodules={lessonsAndSubmodules}
            />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  )
}

export const ModuleContent = ({ lessonsAndSubmodules, handleClickLesson }: ModuleContentProps) => {
  const {
    currentRoleGetter: { isHiddenCustomer: isPreviewMode }
  } = useAuth()

  const renderLessonsAndModules = useMemo(() => {
    return lessonsAndSubmodules.map((item, index) => {
      if (item.type === 'lesson' && item.lesson) {
        return (
          <ModuleLesson
            key={item.lesson.id}
            content={item.lesson}
            index={index}
            handleClickLesson={handleClickLesson}
            lessonsAndSubmodules={lessonsAndSubmodules}
          />
        )
      }

      if (item.type === 'submodule') {
        return (
          <SubmoduleSection
            key={item.submodule?.id}
            submodule={item.submodule}
            isPreviewMode={isPreviewMode}
            handleClickLesson={handleClickLesson}
            lessonsAndSubmodules={lessonsAndSubmodules}
          />
        )
      }
    })
  }, [handleClickLesson, isPreviewMode, lessonsAndSubmodules])

  return <Container>{renderLessonsAndModules}</Container>
}
